import { EnumWidgetType, Multilingual } from "gql/generated";
import { WidgetKey } from "./WidgetAppView.config";
import { getMultilingualString } from "shared/utils";

type FilterableWidget = {
  _id: any;
  type: EnumWidgetType;
  archived: boolean;
  favorited: boolean;
  name: Pick<Multilingual, "en" | "es" | "fr">;
  gallerySettings?: { allMedia?: boolean | null } | null;
};

const isAllMediaWidget = (widget: FilterableWidget) =>
  !!widget.gallerySettings?.allMedia;

/** Keep the All Media system widget pinned above other gallery cards. */
const pinAllMediaFirst = <T extends FilterableWidget>(widgets: T[]): T[] => {
  if (widgets.length < 2) return widgets;
  return [...widgets].sort((a, b) => {
    const aPinned = isAllMediaWidget(a);
    const bPinned = isAllMediaWidget(b);
    if (aPinned === bPinned) return 0;
    return aPinned ? -1 : 1;
  });
};

const filterListsByArchivedStatus = <T extends FilterableWidget>(
  widgets: T[],
  archived: boolean,
) => {
  return widgets.filter((widget) => widget.archived === archived);
};

const filterWidgetsBySearchValue = <T extends FilterableWidget>(
  widgets: T[],
  searchValue: string,
) => {
  return widgets.filter((item) =>
    getMultilingualString(item.name)
      .toLowerCase()
      .includes(searchValue.toLowerCase()),
  );
};

const filterWidgetsByFavoriteStatus = <T extends FilterableWidget>(
  widgets: T[],
  favorited: boolean,
) =>
  widgets.filter(
    (widget) => widget.favorited === favorited && !widget.archived,
  );

export const getFilteredWidgets = <T extends FilterableWidget>(
  widgets: T[],
  key: string,
  searchValue?: string,
): T[] => {
  let filteredWidgets = widgets;
  switch (key) {
    case WidgetKey.All:
      // All Media is a system gallery — keep it out of the All Widgets list.
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        widget => !isAllMediaWidget(widget),
      );
      break;
    case WidgetKey.Operator:
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        (widget) => widget.type === EnumWidgetType.Operator,
      );
      break;
    case WidgetKey.Event:
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        (widget) => widget.type === EnumWidgetType.Event,
      );
      break;
    case WidgetKey.Inquiry:
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        widget => widget.type === EnumWidgetType.Inquiry,
      );
      break;
    case WidgetKey.Gallery:
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        widget => widget.type === EnumWidgetType.Gallery,
      );
      break;
    case WidgetKey.Promotion:
      filteredWidgets = filterListsByArchivedStatus(widgets, false).filter(
        widget => widget.type === EnumWidgetType.Promotion,
      );
      break;
    case WidgetKey.NeedsAttention:
    case WidgetKey.NewMedia:
      // Library sub-views — not widget list filters
      filteredWidgets = [];
      break;
    case WidgetKey.Favorited:
      filteredWidgets = filterWidgetsByFavoriteStatus(widgets, true);
      break;
    case WidgetKey.Archived:
      filteredWidgets = filterListsByArchivedStatus(widgets, true);
      break;
    case WidgetKey.Settings:
      filteredWidgets = [];
      break;
    default:
      filteredWidgets = [];
  }
  if (searchValue) {
    filteredWidgets = filterWidgetsBySearchValue(filteredWidgets, searchValue);
  }
  return pinAllMediaFirst(filteredWidgets);
};
